import { useState, useEffect } from 'react';
import { DashboardApi } from '../../../utils/api';
import type { PlayerSummaryCardsData, PlayerWeeklyActivityData } from '../../../utils/api/Dashboard';
import { getMonday } from '../../../utils/helpers/dashboardDate';

export type DashboardPreset = 'all' | '1W' | '1M' | '3M' | '6M' | '1Y' | 'custom';

export const PRESET_TO_RANGE_KEY: Record<Exclude<DashboardPreset, 'custom'>, string> = {
  all: 'ALL', '1W': '1W', '1M': '1M', '3M': '3M', '6M': '6M', '1Y': '1Y',
};

const dashboardApi = new DashboardApi();

function readSession<T>(key: string, parse: (v: string) => T, fallback: T): T {
  try {
    const v = sessionStorage.getItem(key);
    return v ? parse(v) : fallback;
  } catch {
    return fallback;
  }
}

export function usePlayerDashboardData(playerId?: string) {
  const [rangePreset, setRangePreset] = useState<DashboardPreset>(() =>
    readSession('playerDashboard_preset', (v) => v as DashboardPreset, 'all'),
  );
  const [customStart, setCustomStart] = useState<Date | null>(() =>
    readSession('playerDashboard_customStart', (v) => new Date(v), null),
  );
  const [customEnd, setCustomEnd] = useState<Date | null>(() =>
    readSession('playerDashboard_customEnd', (v) => new Date(v), null),
  );
  const [committedStart, setCommittedStart] = useState<Date | null>(() =>
    readSession('playerDashboard_customStart', (v) => new Date(v), null),
  );
  const [committedEnd, setCommittedEnd] = useState<Date | null>(() =>
    readSession('playerDashboard_customEnd', (v) => new Date(v), null),
  );
  const [currentWeekStart, setCurrentWeekStart] = useState<string>(getMonday());
  const [summaryData, setSummaryData] = useState<PlayerSummaryCardsData | null>(null);
  const [weeklyData, setWeeklyData] = useState<PlayerWeeklyActivityData | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingWeekly, setLoadingWeekly] = useState(true);

  // Persist confirmed custom range across tab navigation
  useEffect(() => {
    try {
      sessionStorage.setItem('playerDashboard_preset', rangePreset);
      if (rangePreset === 'custom') {
        if (committedStart) sessionStorage.setItem('playerDashboard_customStart', committedStart.toISOString());
        if (committedEnd)   sessionStorage.setItem('playerDashboard_customEnd',   committedEnd.toISOString());
      } else {
        sessionStorage.removeItem('playerDashboard_customStart');
        sessionStorage.removeItem('playerDashboard_customEnd');
      }
    } catch { /* ignore */ }
  }, [rangePreset, committedStart, committedEnd]);

  // Sync weekly table to the week of the committed start date
  useEffect(() => {
    if (rangePreset === 'custom' && committedStart) {
      setCurrentWeekStart(getMonday(committedStart));
    }
  }, [committedStart, rangePreset]);

  useEffect(() => {
    if (rangePreset === 'custom') {
      if (!committedStart || !committedEnd) return;
      setLoadingSummary(true);
      dashboardApi.getPlayerSummaryCards({
        playerId,
        startDate: committedStart.toISOString(),
        endDate: committedEnd.toISOString(),
      }).then((res) => {
        if (res.status && res.data) setSummaryData(res.data);
        setLoadingSummary(false);
      });
    } else {
      setLoadingSummary(true);
      dashboardApi.getPlayerSummaryCards({ playerId, rangeKey: PRESET_TO_RANGE_KEY[rangePreset] }).then((res) => {
        if (res.status && res.data) setSummaryData(res.data);
        setLoadingSummary(false);
      });
    }
  }, [rangePreset, committedStart, committedEnd, playerId]);

  useEffect(() => {
    setLoadingWeekly(true);
    dashboardApi.getPlayerWeeklyActivity(currentWeekStart, playerId).then((res) => {
      if (res.status && res.data) setWeeklyData(res.data);
      setLoadingWeekly(false);
    });
  }, [currentWeekStart, playerId]);

  return {
    rangePreset, setRangePreset,
    customStart, setCustomStart,
    customEnd, setCustomEnd,
    committedStart, setCommittedStart,
    committedEnd, setCommittedEnd,
    currentWeekStart, setCurrentWeekStart,
    summaryData,
    weeklyData,
    loadingSummary,
    loadingWeekly,
  };
}
